import axios from 'axios';

class VoiceCallService {
  constructor() {
    // Derive the API host from REACT_APP_API_URL (which includes a trailing /api,
    // e.g. https://care.blackheartassociation.org/api). VoiceCallService endpoints
    // already include the /api prefix, so strip it to get the host root.
    const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
    this.baseURL = apiUrl.replace(/\/api\/?$/, '');
    this.apiClient = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      withCredentials: true, // Send HttpOnly auth cookie
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Handle response errors — delegate auth failures to the global axios interceptor
    // instead of immediately forcing logout on a single 401
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't force logout here — the global axios interceptor in config/axios.js
        // handles 401s with a refresh-then-retry strategy
        return Promise.reject(error);
      }
    );
  }

  // Get all voice calls with filtering and pagination
  async getVoiceCalls(params = {}) {
    try {
      const response = await this.apiClient.get('/api/voice-calls', {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          status: params.status,
          priority: params.priority,
          patientId: params.patientId,
          assignedTo: params.assignedTo,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          search: params.search
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching voice calls:', error);
      throw this.handleError(error);
    }
  }

  // Get voice call statistics
  async getCallStatistics(filters = {}) {
    try {
      const response = await this.apiClient.get('/api/voice-calls/statistics', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching call statistics:', error);
      throw this.handleError(error);
    }
  }

  // Get a specific voice call by ID
  async getVoiceCall(callId) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/${callId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching voice call:', error);
      throw this.handleError(error);
    }
  }

  // Initiate a new voice call (patient-based)
  async initiateVoiceCall(callData) {
    try {
      const response = await this.apiClient.post('/api/voice-calls/initiate', {
        patientId: callData.patientId,
        phoneNumber: callData.phoneNumber,
        callType: callData.callType || 'results-discussion',
        priority: callData.priority || 'medium',
        scheduledFor: callData.scheduledFor,
        context: callData.context,
        metadata: callData.metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error initiating voice call:', error);
      throw this.handleError(error);
    }
  }

  // Quick dial - initiate a call by phone number (not tied to a patient)
  async quickDial(callData) {
    try {
      const response = await this.apiClient.post('/api/voice-calls/quick-dial', {
        phoneNumber: callData.phoneNumber,
        callType: callData.callType || 'personal',
        priority: callData.priority || 'medium',
        context: callData.context || {},
        metadata: callData.metadata || {}
      });
      return response.data;
    } catch (error) {
      console.error('Error initiating quick dial:', error);
      throw this.handleError(error);
    }
  }

  // Get call status
  async getCallStatus(callSid) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/status/${callSid}`);
      return response.data;
    } catch (error) {
      console.error('Error getting call status:', error);
      throw this.handleError(error);
    }
  }

  // End an active voice call
  async endCall(callId, outcome = {}) {
    try {
      const response = await this.apiClient.post(`/api/voice-calls/${callId}/end`, {
        outcome: outcome.result || 'completed',
        notes: outcome.notes || '',
        duration: outcome.duration
      });
      return response.data;
    } catch (error) {
      console.error('Error ending voice call:', error);
      throw this.handleError(error);
    }
  }

  // Schedule a voice call for later
  async scheduleVoiceCall(scheduleData) {
    try {
      const response = await this.apiClient.post('/api/voice-calls/schedule', {
        patientId: scheduleData.patientId,
        phoneNumber: scheduleData.phoneNumber,
        scheduledFor: scheduleData.scheduledFor,
        callType: scheduleData.callType || 'results-discussion',
        priority: scheduleData.priority || 'medium',
        assignedTo: scheduleData.assignedTo,
        notes: scheduleData.notes,
        reminders: scheduleData.reminders
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling voice call:', error);
      throw this.handleError(error);
    }
  }

  // Update voice call status
  async updateCallStatus(callId, statusData) {
    try {
      const response = await this.apiClient.patch(`/api/voice-calls/${callId}/status`, {
        status: statusData.status,
        outcome: statusData.outcome,
        notes: statusData.notes,
        duration: statusData.duration,
        recording: statusData.recording,
        transcript: statusData.transcript
      });
      return response.data;
    } catch (error) {
      console.error('Error updating call status:', error);
      throw this.handleError(error);
    }
  }

  // Get call history for a specific patient
  async getPatientCallHistory(patientId, params = {}) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/patient/${patientId}/history`, {
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          status: params.status,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient call history:', error);
      throw this.handleError(error);
    }
  }

  // Get call recording URL
  async getCallRecording(callId) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/${callId}/recording`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call recording:', error);
      throw this.handleError(error);
    }
  }

  // Get call transcript
  async getCallTranscript(callId) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/${callId}/transcript`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call transcript:', error);
      throw this.handleError(error);
    }
  }

  // Cancel a scheduled call
  async cancelCall(callId, reason) {
    try {
      const response = await this.apiClient.patch(`/api/voice-calls/${callId}/cancel`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error canceling call:', error);
      throw this.handleError(error);
    }
  }

  // Retry a failed call
  async retryCall(callId, options = {}) {
    try {
      const response = await this.apiClient.post(`/api/voice-calls/${callId}/retry`, {
        scheduledFor: options.scheduledFor,
        maxAttempts: options.maxAttempts,
        notes: options.notes
      });
      return response.data;
    } catch (error) {
      console.error('Error retrying call:', error);
      throw this.handleError(error);
    }
  }

  // Get AI agent performance metrics
  async getAgentMetrics(params = {}) {
    try {
      const response = await this.apiClient.get('/api/voice-calls/metrics/agent', {
        params: {
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          agentId: params.agentId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching agent metrics:', error);
      throw this.handleError(error);
    }
  }

  // Update call priority
  async updateCallPriority(callId, priority) {
    try {
      const response = await this.apiClient.patch(`/api/voice-calls/${callId}/priority`, {
        priority
      });
      return response.data;
    } catch (error) {
      console.error('Error updating call priority:', error);
      throw this.handleError(error);
    }
  }

  // Assign call to agent/provider
  async assignCall(callId, assignedTo) {
    try {
      const response = await this.apiClient.patch(`/api/voice-calls/${callId}/assign`, {
        assignedTo
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning call:', error);
      throw this.handleError(error);
    }
  }

  // Add notes to a call
  async addCallNote(callId, note) {
    try {
      const response = await this.apiClient.post(`/api/voice-calls/${callId}/notes`, {
        note,
        timestamp: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('Error adding call note:', error);
      throw this.handleError(error);
    }
  }

  // Get call quality analysis
  async getCallQuality(callId) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/${callId}/quality`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call quality:', error);
      throw this.handleError(error);
    }
  }

  // Search calls by criteria
  async searchCalls(searchCriteria) {
    try {
      const response = await this.apiClient.post('/api/voice-calls/search', {
        query: searchCriteria.query,
        filters: searchCriteria.filters,
        sort: searchCriteria.sort,
        page: searchCriteria.page || 1,
        limit: searchCriteria.limit || 20
      });
      return response.data;
    } catch (error) {
      console.error('Error searching calls:', error);
      throw this.handleError(error);
    }
  }

  // Get upcoming scheduled calls
  async getUpcomingCalls(params = {}) {
    try {
      const response = await this.apiClient.get('/api/voice-calls/upcoming', {
        params: {
          date: params.date || new Date().toISOString().split('T')[0],
          limit: params.limit || 50,
          assignedTo: params.assignedTo
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming calls:', error);
      throw this.handleError(error);
    }
  }

  // Get call status updates (for real-time monitoring)
  async getCallStatusUpdates(callId) {
    try {
      const response = await this.apiClient.get(`/api/voice-calls/${callId}/status-updates`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call status updates:', error);
      throw this.handleError(error);
    }
  }

  // Bulk operations
  async bulkUpdateCalls(callIds, updateData) {
    try {
      const response = await this.apiClient.patch('/api/voice-calls/bulk-update', {
        callIds,
        updateData
      });
      return response.data;
    } catch (error) {
      console.error('Error updating calls in bulk:', error);
      throw this.handleError(error);
    }
  }

  // Export call data
  async exportCallData(filters = {}, format = 'csv') {
    try {
      const response = await this.apiClient.get('/api/voice-calls/export', {
        params: {
          ...filters,
          format
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting call data:', error);
      throw this.handleError(error);
    }
  }

  // WebSocket connection for real-time call updates
  connectToCallUpdates(callId, onUpdate, onError) {
    if (!window.WebSocket) {
      console.error('WebSocket not supported');
      return null;
    }

    const wsUrl = `${this.baseURL.replace('http', 'ws')}/ws/voice-calls/${callId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('Connected to call updates WebSocket');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onUpdate(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      if (onError) onError(error);
    };

    ws.onclose = () => {
      console.log('WebSocket connection closed');
    };

    return ws;
  }

  // Error handling helper
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        message: data.message || 'An error occurred',
        status,
        details: data.details || null
      };
    } else if (error.request) {
      // Network error
      return {
        message: 'Network error - please check your connection',
        status: 0,
        details: null
      };
    } else {
      // Other error
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
        details: null
      };
    }
  }

  // Get Twilio Access Token for browser-based calling
  async getAccessToken(identity) {
    try {
      const response = await this.apiClient.get('/api/voice-calls/access-token', {
        params: { identity }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw this.handleError(error);
    }
  }

  // Whether inbound calls ring staff browsers (and the identity to register as)
  async getInboundConfig() {
    const response = await this.apiClient.get('/api/voice-calls/inbound-config');
    return response.data;
  }
}

// Export singleton instance
export default new VoiceCallService();